import { inject, Injectable, InjectionToken } from '@angular/core';
import type { Observable } from 'rxjs';

import { switchMap } from 'rxjs/operators';

import type { BleConnectionState, BleDeviceInfoExtended, BleServiceResponse, DeviceData } from '../../interfaces/ble.interface';
import { MockBleDeviceManagerService } from '../../../mocks/hunting-device/mock-ble-device-manager.service';
import { MockHuntingDeviceGate } from '../../../mocks/hunting-device/mock-hunting-device-gate.service';

import type { BleDeviceManagerService } from './ble-device-manager.service';

// Separate token for the real manager so the facade can hold a real instance while the app-wide
// BleDeviceManagerService token resolves to the facade itself (see app.config.ts).
export const REAL_BLE_MANAGER = new InjectionToken<BleDeviceManagerService>('REAL_BLE_MANAGER');

/**
 * Routes every BLE call/stream to the real manager or the mock based on MockHuntingDeviceGate, so
 * the dev toggle swaps them at runtime instead of build time. Deliberately does not extend
 * BleDeviceManagerService (that would re-run its BLE-initializing constructor).
 */
@Injectable()
export class BleDeviceManagerFacade {
  // services
  private readonly real = inject(REAL_BLE_MANAGER);
  private readonly mock = inject(MockBleDeviceManagerService);
  private readonly gate = inject(MockHuntingDeviceGate);

  // public observables
  public readonly connectionState$: Observable<BleConnectionState> = this.gate.active$.pipe(
    switchMap(active => (active ? this.mock.connectionState$ : this.real.connectionState$))
  );
  public readonly connectedDevice$: Observable<BleDeviceInfoExtended | undefined> = this.gate.active$.pipe(
    switchMap(active => (active ? this.mock.connectedDevice$ : this.real.connectedDevice$))
  );
  public readonly deviceData$: Observable<DeviceData> = this.gate.active$.pipe(switchMap(active => (active ? this.mock.deviceData$ : this.real.deviceData$)));
  public readonly scannedDevices$: Observable<Array<BleDeviceInfoExtended>> = this.gate.active$.pipe(
    switchMap(active => (active ? this.mock.scannedDevices$ : this.real.scannedDevices$))
  );
  public readonly bleEnabled$: Observable<boolean> = this.gate.active$.pipe(switchMap(active => (active ? this.mock.bleEnabled$ : this.real.bleEnabled$)));
  public readonly onUnexpectedDisconnect$ = this.gate.active$.pipe(
    switchMap(active => (active ? this.mock.onUnexpectedDisconnect$ : this.real.onUnexpectedDisconnect$))
  );

  public scanForDevices$(): Observable<void> {
    return this.gate.isActive ? this.mock.scanForDevices$() : this.real.scanForDevices$();
  }

  /**
   * Ensures a device is known (returns immediately if already scanned, otherwise waits for a
   * targeted scan). Used by the auto-reconnect flow, which needs the device in the scan map
   * before connecting. The real manager performs the scan; the mock resolves to the fake Cannes
   * device so the auto-reconnect retry loop can run against the dev toggle too.
   */
  public waitForDevice$(deviceId: string, timeoutMs?: number): Observable<BleDeviceInfoExtended> {
    return this.gate.isActive ? this.mock.waitForDevice$(deviceId, timeoutMs) : this.real.waitForDevice$(deviceId, timeoutMs);
  }

  public connectToDevice$(deviceId: string): Observable<BleServiceResponse<BleDeviceInfoExtended>> {
    return this.gate.isActive ? this.mock.connectToDevice$(deviceId) : this.real.connectToDevice$(deviceId);
  }

  public disconnect$(): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.disconnect$() : this.real.disconnect$();
  }

  public readBatteryLevel$(): Observable<BleServiceResponse<number>> {
    return this.gate.isActive ? this.mock.readBatteryLevel$() : this.real.readBatteryLevel$();
  }

  public readBrightnessLevel$(): Observable<BleServiceResponse<number>> {
    return this.gate.isActive ? this.mock.readBrightnessLevel$() : this.real.readBrightnessLevel$();
  }

  public writeBrightnessLevel$(level: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeBrightnessLevel$(level) : this.real.writeBrightnessLevel$(level);
  }

  public writeContrastLevel$(level: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeContrastLevel$(level) : this.real.writeContrastLevel$(level);
  }

  public writeImageMode$(mode: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeImageMode$(mode) : this.real.writeImageMode$(mode);
  }

  public writeConnectivityEnabled$(enabled: boolean): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeConnectivityEnabled$(enabled) : this.real.writeConnectivityEnabled$(enabled);
  }

  public writeWifiEnabled$(enabled: boolean): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeWifiEnabled$(enabled) : this.real.writeWifiEnabled$(enabled);
  }

  public readAccessPointSSID$(): Observable<BleServiceResponse<string>> {
    return this.gate.isActive ? this.mock.readAccessPointSSID$() : this.real.readAccessPointSSID$();
  }

  public startStreaming$(): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.startStreaming$() : this.real.startStreaming$();
  }

  public stopStreaming$(): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.stopStreaming$() : this.real.stopStreaming$();
  }

  public readStreamingStatus$(): Observable<BleServiceResponse<boolean>> {
    return this.gate.isActive ? this.mock.readStreamingStatus$() : this.real.readStreamingStatus$();
  }

  public writeRecordVideo$(recording: boolean): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeRecordVideo$(recording) : this.real.writeRecordVideo$(recording);
  }

  public takePicture$(): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.takePicture$() : this.real.takePicture$();
  }

  public writeAccessPointPassphrase$(passphrase: string): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeAccessPointPassphrase$(passphrase) : this.real.writeAccessPointPassphrase$(passphrase);
  }

  public writeWifiMode$(mode: number): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeWifiMode$(mode) : this.real.writeWifiMode$(mode);
  }

  public loadSettingsData$(): Observable<{ brightnessLevel: number; wifiEnabled: boolean; contrastLevel: number; imageMode: number }> {
    return this.gate.isActive ? this.mock.loadSettingsData$() : this.real.loadSettingsData$();
  }

  public readDeviceInfo$(): Observable<BleServiceResponse<Partial<DeviceData>>> {
    return this.gate.isActive ? this.mock.readDeviceInfo$() : this.real.readDeviceInfo$();
  }

  public loadInitialData$(): Observable<void> {
    return this.gate.isActive ? this.mock.loadInitialData$() : this.real.loadInitialData$();
  }

  public readBleLocalName$(): Observable<BleServiceResponse<string>> {
    return this.gate.isActive ? this.mock.readBleLocalName$() : this.real.readBleLocalName$();
  }

  public writeBleLocalName$(name: string): Observable<BleServiceResponse<void>> {
    return this.gate.isActive ? this.mock.writeBleLocalName$(name) : this.real.writeBleLocalName$(name);
  }

  public stopScan(): Promise<void> {
    return this.gate.isActive ? this.mock.stopScan() : this.real.stopScan();
  }

  /**
   * Native BLE-stack initialization is always a real-manager concern — the sample mock has no stack
   * to warm up, so this is never routed through the gate/mock. Auto-reconnect calls it to avoid
   * trusting a stale `isEnabled` flag on cold start.
   */
  public async ensureBleReady$(): Promise<boolean> {
    return this.real.ensureBleReady$();
  }

  public async hasBlePermissions$(): Promise<boolean> {
    return this.real.hasBlePermissions$();
  }

  public async validateCurrentConnection$(): Promise<boolean> {
    return this.gate.isActive ? this.mock.isConnected : this.real.validateCurrentConnection$();
  }

  public get isConnected(): boolean {
    return this.gate.isActive ? this.mock.isConnected : this.real.isConnected;
  }

  public get isBleEnabled(): boolean {
    return this.gate.isActive ? this.mock.isBleEnabled : this.real.isBleEnabled;
  }

  public get currentConnectionState(): BleConnectionState {
    return this.gate.isActive ? this.mock.currentConnectionState : this.real.currentConnectionState;
  }

  public getScannedDevices(): Array<BleDeviceInfoExtended> {
    return this.gate.isActive ? this.mock.getScannedDevices() : this.real.getScannedDevices();
  }
}

// Compile-time guard: fails the build if the facade drifts from the real manager's public API.
type _PublicApi = Pick<
  BleDeviceManagerService,
  | 'connectionState$'
  | 'connectedDevice$'
  | 'deviceData$'
  | 'scannedDevices$'
  | 'bleEnabled$'
  | 'scanForDevices$'
  | 'connectToDevice$'
  | 'disconnect$'
  | 'readBatteryLevel$'
  | 'readBrightnessLevel$'
  | 'writeBrightnessLevel$'
  | 'writeContrastLevel$'
  | 'writeImageMode$'
  | 'writeConnectivityEnabled$'
  | 'writeWifiEnabled$'
  | 'readAccessPointSSID$'
  | 'startStreaming$'
  | 'stopStreaming$'
  | 'readStreamingStatus$'
  | 'writeRecordVideo$'
  | 'takePicture$'
  | 'writeAccessPointPassphrase$'
  | 'writeWifiMode$'
  | 'loadSettingsData$'
  | 'readDeviceInfo$'
  | 'loadInitialData$'
  | 'ensureBleReady$'
  | 'hasBlePermissions$'
  | 'validateCurrentConnection$'
  | 'readBleLocalName$'
  | 'writeBleLocalName$'
  | 'stopScan'
  | 'isConnected'
  | 'currentConnectionState'
>;
const _assert: _PublicApi = undefined as unknown as BleDeviceManagerFacade;

// Fails the build when the real manager gains a public member the facade does not forward —
// a missing forward otherwise surfaces only as a runtime TypeError (the app-wide token
// resolves to the facade).
type _MissingFromFacade = Exclude<keyof BleDeviceManagerService, keyof BleDeviceManagerFacade>;
const _assertNoneMissing: Record<_MissingFromFacade, never> = {};
